import { Button, message } from "antd";
import dayjs from "dayjs";
import { ErrorMessage, Form, Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useGetData from "../../api/useGetData";
import DatePickerInput from "../../common/DatePickerInput";
import SelectInput from "../../common/SelectInput";
import TimePicker from "../../common/TimePicker";
import { isPointInPolygon } from "../../utils/mapUtils";
import { RideByHourRideDetailsValidation } from "../../utils/validations";
import MapModal from "../MapModal/MapModal";
import AgentUserSelect from "../UsersSelect";
import { RideByHourContext } from "./RideByHourContext";
import { bookingTracking } from "../../utils/mixpanel";
import usePostData from "../../api/usePostData";

const service = "Book Vehicle In Hours";

const RideDetailsStep = () => {
  const { t } = useTranslation("global");
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const user = useSelector((state) => state.authData.user);
  const isRegistered = !!user;
  const { setActiveTab, firstStepValues, setFirstStepValues, setErrorMessage, errorMessage } =
    useContext(RideByHourContext);

  const { data: cities } = useGetData({ route: "api/method/airport_transport.api.bookings.get_city", params: { service, }, });
  const { data: hourLimitsData } = useGetData({ route: "api/method/airport_transport.api.bookings.get_booking_hour_limit", params: {}, });
  const { postData: checkTripBookingTime } = usePostData();

  const hoursOptions = [
    ...Array(hourLimitsData.max_hours - hourLimitsData.min_hours + 1 || 1),
  ].map((_, i) => {
    return {
      label: hourLimitsData.min_hours + i,
      value: hourLimitsData.min_hours + i,
    };
  });

  const bookingSettings = useSelector(
    (state) => state.cachedRequests.bookingSettings
  );

  const bookingAllowedTime = bookingSettings.booking_allowed_time / 60;

  const { data: zonesData, getData: getZonesData } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_zones",
    notLoadData: true,
  });
  useEffect(() => {
    if (firstStepValues.arrivalCity) {
      getZonesData({
        params: {
          city: firstStepValues.arrivalCity,
          service,
        },
      });
    }
  }, [firstStepValues]);

  const resetPickup = (setFieldValue) => {
    setFieldValue("location", "");
    setFieldValue("locationCoords", null);
  };
// _________________________ C H E C K  T R I P  E X I S T  B E F O R E  B O O K I N G ______________________________//
  const Check_Trip_Exist_Before = async (date, time) => {
    try {
      const response = await checkTripBookingTime({
        route: "api/method/airport_transport.api.bookings.check_booking",
        data: {arrival_date:date.format("YYYY-MM-DD"), arrival_time: time.format("HH:mm")},
        customError: true
      });
      if (response.status !== 200) {
        if(response?.error?.includes("You already have")){
          message.error(t("have_a_trip_for_this_date_and_time")); 
          setErrorMessage(response?.error);
        }
      }
      else{
        return true;
      }
    } catch (error) {
      message.error("Booking error occurred");
      return false;
    }
  };
// _______________________________________________________________________________________________//

  const onSubmit = async (values) => {
   if(isRegistered){
      const bookingTripInitially = await Check_Trip_Exist_Before(values.arrivalDate, values.arrivalTime);
        if (!bookingTripInitially) {
          return; // Stop execution if inializing booking fails
        }
    }
    const zone = (
      zonesData.find((zone) =>
        isPointInPolygon(values.locationCoords, zone.map)
      ) || zonesData?.[0]
    )?.name;
    setFirstStepValues({ ...values, zone });
    bookingTracking({
      user: user?.email,
      service: "Hours",
      step: "Ride Details Step",
    });
    setActiveTab(2);
  };

  return (
    <div>
      <Formik
        initialValues={{
          agentUser: firstStepValues.agentUser,
          hours: firstStepValues.hours,
          arrivalCity: firstStepValues.arrivalCity,
          arrivalDate:
            firstStepValues.arrivalDate ||
            dayjs().add(bookingAllowedTime, "hours"),
          arrivalTime:
            firstStepValues.arrivalTime ||
            dayjs().add(bookingAllowedTime, "hours"),
          location: firstStepValues.location,
          locationCoords: firstStepValues.locationCoords,
        }}
        onSubmit={onSubmit}
        validationSchema={RideByHourRideDetailsValidation(
          t,
          bookingAllowedTime,
          user?.agent
        )}
      >
        {({ setFieldValue, values, errors, touched, setTouched }) => (
          <Form className="flex flex-col gap-4">
            {user?.agent && (
              <AgentUserSelect
                label={t("user_text")}
                name={"agentUser"}
                onChange={(user) => {
                  setFieldValue("agentUser", user);
                }}
                placeholder={t("hero.select_user")}
              />
            )}
            <SelectInput
              name="hours"
              label={t("hero.booking_by_hours")}
              placeholder={t("hero.booking_by_hours")}
              options={hoursOptions}
              onChange={(date) => {
                setFieldValue("hours", date);
              }}
            />
            <SelectInput
              name="arrivalCity"
              label={t("hero.arrival_city_text")}
              placeholder={t("hero.select_arrival_city")}
              options={cities?.map((c) => ({ value: c, label: c }))}
              onChange={(city) => {
                getZonesData({
                  params: {
                    city,
                    service,
                  },
                });
                resetPickup(setFieldValue);
                setFieldValue("arrivalCity", city);
              }}
            />
            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalDate">{t("hero.arrival_date_text")}</label>
              <DatePickerInput
                format={"YYYY-MM-DD"}
                value={values.arrivalDate}
                onChange={(date) => {
                  setFieldValue("arrivalDate", date);
                  setErrorMessage("");
                }}
                disabledDate={(current) => {
                  return (
                    current &&
                    current <
                      dayjs().add(bookingAllowedTime, "hours").startOf("day")
                  );
                }}
                name="arrivalDate"
              />
              {errors.arrivalDate && touched.arrivalDate && (
                <p className="text-text_warning text-sm mt-1 text-start">
                  {t(errors.arrivalDate)}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalTime">{t("hero.arrival_time_text")}</label>
              <TimePicker
                value={values.arrivalTime}
                onChange={(time) => {
                  setFieldValue("arrivalTime", time);
                  setErrorMessage("");
                }}
                name="arrivalTime"
              />
              <ErrorMessage name="arrivalTime" render={renderErrorMessage} />
            </div>
            {errorMessage && <p className="font-[500] text-text_warning text-[15px] mt-1 text-start">{t("have_a_trip_for_this_date_and_time")}</p>}
            <div className="flex flex-col gap-1">
              <label htmlFor="map">{t("hero.select_your_text")}</label>
              <button
                onClick={() => {
                  setMapModalOpen(true);
                  setTouched("location", true);
                }}
                className="flex justify-between w-full bg-background_white text-gray-700 border border-border_color font-medium rounded-lg text-sm px-3 py-3 text-left disabled:bg-background_grey"
                type="button"
                disabled={!values.arrivalCity}
              >
                <span>{t("hero.place_of_arrival_text")}</span>
                <img src="./assets/map/map.png" alt="map" />
              </button>
              {touched.location && errors.location && (
                <span
                  style={{
                    color: "#ff4d4f",
                    fontSize: "small",
                  }}
                >
                  {t(errors.location)}
                </span>
              )}
            </div>
            {mapModalOpen && (
              <MapModal
                cityName={values.arrivalCity}
                closeModal={() => {
                  setMapModalOpen(false);
                }}
                rideName="rideByHour"
                isModalOpen={mapModalOpen}
                formValues={values}
                confirm={({ location, locationCoords }) => {
                  setFieldValue("location", location);
                  setFieldValue("locationCoords", locationCoords);
                }}
                zoneCoords={zonesData}
              />
            )}
            <Button
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 mt-3"
              type="button"
              htmlType="submit"
              disabled={mapModalOpen}
            >
              {t("next_text")}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RideDetailsStep;

const renderErrorMessage = (msg) => (
  <span
    style={{
      color: "#ff4d4f",
      fontSize: "small",
    }}
  >
    {msg}
  </span>
);
